import React from "react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { api, fileUrl } from "../lib/api";

/**
 * Instagram-feel grid — 6 recent visual moments pulled from published project galleries.
 * A quiet nod to social presence without cluttering the site with a real embed.
 */
export default function InstagramFeed() {
  const { data: projects = [] } = useQuery({
    queryKey: ["projects", "public"],
    queryFn: async () => (await api.get("/projects")).data,
  });

  // Build 6-tile pool from all project galleries (skip hero)
  const tiles = [];
  for (const p of projects) {
    for (const img of p.gallery || []) {
      if (tiles.length >= 6) break;
      tiles.push({ id: img.id, src: img.variants.thumb, alt: `${p.title} · ${p.location}`, slug: p.slug });
    }
    if (tiles.length >= 6) break;
  }

  if (tiles.length === 0) return null;

  return (
    <section data-testid="instagram-feed" className="container-w py-24 md:py-40 relative">
      <div className="flex items-baseline justify-between mb-10">
        <div>
          <div className="mono-label mb-2 text-ash">Elsewhere</div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight">Instagram, quietly.</h2>
        </div>
        <a
          href="https://instagram.com/thewhiteprojectco"
          target="_blank"
          rel="noreferrer"
          data-testid="instagram-link"
          className="mono-label link-underline"
        >
          @thewhiteprojectco →
        </a>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5 md:gap-2">
        {tiles.map((t, i) => (
          <motion.a
            key={t.id}
            href="https://instagram.com/thewhiteprojectco"
            target="_blank"
            rel="noreferrer"
            className="block relative overflow-hidden aspect-square"
            data-cursor="hover"
            data-testid={`instagram-tile-${i}`}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
          >
            <img
              src={fileUrl(t.src)}
              alt={t.alt}
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500 bg-ink/30 flex items-end p-2">
              <span className="mono-label text-paper" style={{ fontSize: "10px" }}>{t.alt}</span>
            </div>
          </motion.a>
        ))}
      </div>
    </section>
  );
}
